// The media panel's lists (live's Uploads, Images and Videos): the brand's library files and its
// jobs' delivered outputs as tiles, each list read page by page, newest first.
import { useInfiniteQuery, type QueryClient } from "@tanstack/react-query";

import { getGetAssetQueryKey } from "../../api/generated/endpoints/assets/assets";
import {
  getGetJobQueryKey,
  getGetTeamActorQueryKey,
  getListActorsQueryKey,
  getListJobsQueryKey,
  listActors,
  listJobs,
} from "../../api/generated/endpoints/generation/generation";
import type { AiActor, Asset, AssetSource, Job } from "../../api/generated/model";
import { actorMedia, assetMedia, outputMedia, type SourceMedia } from "./media";
import type { ClipSource, MediaKind } from "./timeline";

/**
 * What made a file, as live's filter names it: "creative" a generation, "manual" an upload,
 * "product" a product's picture, "avatar" one of the team's actors. A brand's logo is "other".
 */
export type BinType = "creative" | "manual" | "product" | "avatar" | "other";

export interface BinItem {
  key: string;
  /** What it adds: a video, a still or a sound. */
  kind: MediaKind;
  type: BinType;
  /** When it was made, in ms since the epoch: every list is newest first. */
  at: number;
  /** The job output a library file was saved from (`<job id>:<index>`), to show it once. */
  savedFrom: string | null;
  /** Who made a library file (the one who may delete it, besides an admin); null for an output. */
  createdBy: string | null;
  source: ClipSource;
  media: SourceMedia;
  /** Seeds the one-source read the timeline uses, so a new clip shows its name at once. */
  prime: (queryClient: QueryClient) => void;
}

const TYPE_OF_SOURCE: Record<AssetSource, BinType> = {
  upload: "manual",
  url_import: "manual",
  generation: "creative",
  product_image: "product",
  brand_logo: "other",
};

function primeOnce(
  queryClient: QueryClient,
  key: readonly unknown[],
  data: Asset | Job | AiActor,
): void {
  if (queryClient.getQueryData(key) === undefined) queryClient.setQueryData(key, data);
}

/** A library file as a tile. */
export function assetItem(teamId: string, brandId: string, asset: Asset): BinItem {
  return {
    key: asset.id,
    kind: asset.kind,
    type: TYPE_OF_SOURCE[asset.source],
    at: Date.parse(asset.created_at),
    savedFrom: asset.origin === null ? null : `${asset.origin.job_id}:${asset.origin.output_index}`,
    createdBy: asset.created_by,
    source: { kind: "asset", assetId: asset.id },
    media: assetMedia(asset),
    prime: (queryClient) =>
      primeOnce(queryClient, getGetAssetQueryKey(teamId, brandId, asset.id), asset),
  };
}

/** A succeeded job's delivered outputs of one kind, as tiles. */
export function outputItems(teamId: string, job: Job, kind: MediaKind): BinItem[] {
  if (job.status !== "succeeded") return [];
  return job.outputs.flatMap((output) => {
    const media = outputMedia(job, output.index);
    if (media?.kind !== kind) return [];
    return [
      {
        key: `${job.id}:${output.index}`,
        kind,
        type: "creative" as const,
        at: Date.parse(job.created_at),
        savedFrom: null,
        createdBy: null,
        source: { kind: "output", jobId: job.id, index: output.index },
        media,
        prime: (queryClient: QueryClient) =>
          primeOnce(queryClient, getGetJobQueryKey(teamId, job.id), job),
      },
    ];
  });
}

/** One of the team's actors as a picture's tile (live's Avatar). */
export function actorItem(teamId: string, actor: AiActor): BinItem {
  return {
    key: `actor:${actor.id}`,
    kind: "image",
    type: "avatar",
    at: Date.parse(actor.created_at),
    savedFrom: null,
    createdBy: actor.created_by,
    source: { kind: "actor", actorId: actor.id },
    media: actorMedia(actor),
    prime: (queryClient) =>
      primeOnce(queryClient, getGetTeamActorQueryKey(teamId, actor.id), actor),
  };
}

/** One newest-first list as read so far. */
export interface Stream {
  /** Its tiles (a page of jobs may hold none of a tab's kind). */
  items: readonly BinItem[];
  /** When the oldest row read so far was made (Infinity before any is read). */
  readTo: number;
  more: boolean;
}

/** The oldest of rows read newest first, as a stream's `readTo`. */
export function readTo(rows: readonly { created_at: string }[]): number {
  const last = rows.at(-1);
  return last === undefined ? Infinity : Date.parse(last.created_at);
}

/**
 * Several newest-first lists, each read page by page, as one newest-first list. An item is shown
 * only once nothing still unread can come before it: down to the newest `readTo` of the lists
 * with more pages. `next` is that list, the one to read on (null once all are read). An output
 * saved into the library shows once, as the library's file.
 */
export function mergeNewest(streams: readonly Stream[]): { items: BinItem[]; next: number | null } {
  let next: number | null = null;
  let bound = -Infinity;
  streams.forEach((stream, at) => {
    if (stream.more && (next === null || stream.readTo > bound)) {
      next = at;
      bound = stream.readTo;
    }
  });
  const saved = new Set(streams.flatMap((stream) => stream.items.map((item) => item.savedFrom)));
  const items = streams
    .flatMap((stream) => stream.items)
    .filter((item) => item.at >= bound && !saved.has(item.key))
    .sort((one, other) => other.at - one.at);
  return { items, next };
}

const JOBS_PAGE = 50;
/** Live's Avatar lists 24 at a time, as its Images do. */
const ACTORS_PAGE = 24;

/**
 * The team's actors, from every brand, newest first, page by page (live's Avatar lists the team's
 * saved avatars, its view joining every brand), searched by name as My Actors is.
 */
export function useTeamActors(teamId: string, brandId: string, search: string, enabled: boolean) {
  const params = { kind: "team" as const, ...(search === "" ? {} : { search }) };
  return useInfiniteQuery({
    enabled,
    // "pages" keeps these pages apart from the AI Actors page's own of the same params.
    queryKey: [...getListActorsQueryKey(teamId, brandId, params), "bin"],
    queryFn: ({ pageParam, signal }) =>
      listActors(
        teamId,
        brandId,
        { ...params, limit: ACTORS_PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
}

/**
 * The brand's jobs, newest first, page by page: the Images and Videos tabs show their delivered
 * outputs. The event stream reads every job list of the team again by the key's path, so a new
 * output reaches these pages.
 */
export function useBrandJobs(teamId: string, brandId: string) {
  const params = { status: "all" as const, brand_id: brandId };
  return useInfiniteQuery({
    // "pages" keeps these pages apart from a plain `useListJobs` of the same params.
    queryKey: [...getListJobsQueryKey(teamId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listJobs(
        teamId,
        { ...params, limit: JOBS_PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
}
